/* eslint-disable import/no-extraneous-dependencies */
import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import { Fragment, Node as ProseMirrorNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { CommandProps } from '@tiptap/core';

const hasHardBreak = (node: ProseMirrorNode) => {
  let found = false;

  node.descendants((child) => {
    if (child.type.name === 'hardBreak') {
      found = true;
      return false;
    }

    return !found;
  });

  return found;
};

const splitParagraphAtHardBreaks = (node: ProseMirrorNode) => {
  const content: ProseMirrorNode[][] = [[]];

  node.forEach((child) => {
    if (child.type.name === 'hardBreak') {
      content.push([]);
      return;
    }

    content[content.length - 1].push(child);
  });

  while (content.length > 1 && content[content.length - 1].length === 0) {
    content.pop();
  }

  return content.map((itemContent) => node.type.create(node.attrs, Fragment.fromArray(itemContent)));
};

const isInsideListItem = ({ tr }: CommandProps, pos: number) => {
  const $pos = tr.doc.resolve(pos);
  const { depth: startDepth } = $pos;

  for (let depth = startDepth; depth > 0; depth -= 1) {
    if ($pos.node(depth).type.name === 'listItem') {
      return true;
    }
  }

  return false;
};

const splitSelectedParagraphsAtHardBreaks = (props: CommandProps) => {
  const { tr } = props;
  const { selection } = tr;
  const replacements: Array<{ from: number; to: number; nodes: ProseMirrorNode[] }> = [];

  const addReplacement = (node: ProseMirrorNode, pos: number) => {
    if (node.type.name !== 'paragraph' || isInsideListItem(props, pos + 1) || !hasHardBreak(node)) {
      return;
    }

    replacements.push({
      from: pos,
      to: pos + node.nodeSize,
      nodes: splitParagraphAtHardBreaks(node),
    });
  };

  if (selection.empty && selection.$from.parent.type.name === 'paragraph') {
    addReplacement(selection.$from.parent, selection.$from.before());
  } else {
    tr.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
      addReplacement(node, pos);
    });
  }

  const replacementSelection =
    selection.empty && replacements.length === 1
      ? {
          from: replacements[0].from,
          size: replacements[0].nodes.reduce((size, node) => size + node.nodeSize, 0),
        }
      : undefined;

  replacements.reverse().forEach(({ from, to, nodes }) => {
    tr.replaceWith(from, to, nodes);
  });

  if (replacementSelection) {
    tr.setSelection(
      TextSelection.create(
        tr.doc,
        replacementSelection.from + 1,
        replacementSelection.from + replacementSelection.size - 1,
      ),
    );
  }

  return true;
};

export const HardBreakAwareBulletList = BulletList.extend({
  addCommands() {
    return {
      toggleBulletList:
        () =>
        ({ chain, commands }) => {
          if (this.editor.isActive(this.name)) {
            return commands.toggleList(this.name, this.options.itemTypeName, this.options.keepMarks);
          }

          return chain()
            .command(splitSelectedParagraphsAtHardBreaks)
            .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks)
            .run();
        },
    };
  },
});

export const HardBreakAwareOrderedList = OrderedList.extend({
  addCommands() {
    return {
      toggleOrderedList:
        () =>
        ({ chain, commands }) => {
          if (this.editor.isActive(this.name)) {
            return commands.toggleList(this.name, this.options.itemTypeName, this.options.keepMarks);
          }

          return chain()
            .command(splitSelectedParagraphsAtHardBreaks)
            .toggleList(this.name, this.options.itemTypeName, this.options.keepMarks)
            .run();
        },
    };
  },
});
